// src/navigations/AdminNav.js
import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import * as Dialog from "@radix-ui/react-dialog";
import {
  Menu,
  X,
  Users,
  PieChart,
  Settings,
  LogOut,
  FileText,
  ChevronDown,
  Building2,
  MessageSquare,
  HelpingHand,
  Wifi,
  WifiOff,
  GalleryHorizontal,
  Bell,
} from "lucide-react";
import db from "../db";
import useOnlineStatus from "../hooks/useOnlineStatus";
import { useNotifications } from "../contexts/NotificationContext";
import FloatingAccessibilityControls from "../components/FloatingAccessibilityControls";
import { useGlobalGalleryNotifications } from "../hooks/useGlobalGalleryNotifications";
import { useGlobalNewsNotifications } from "../hooks/useGlobalNewsNotifications";
import { useLanguageContext } from "./homenav";
import { useRef } from "react";

export default function AdminNav() {
  const { language, changeLanguage, t } = useLanguageContext();
  const [isOpen, setIsOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const languageRef = useRef(null);
  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
  const currentUserId = currentUser?.id || null;
  const [membersCount, setMembersCount] = useState(0);
  const [usersCount, setUsersCount] = useState(0);
  const [opdsCount, setOpdsCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [rejectedCount, setRejectedCount] = useState(0);
  const [membersOpen, setMembersOpen] = useState(false);
  const [opdsOpen, setOpdsOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  const [managementOpen, setManagementOpen] = useState(false);
  const [supportsOpen, setSupportsOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [chatConversations, setChatConversations] = useState([]);
  const notifRef = useRef(null);
  const sidebarRef = useRef(null);
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();
  const { notifications, unreadCount, markAsRead, markAllAsRead, removeNotification } = useNotifications();
  // Filter notifications to only show gallery and news notifications for admin users
  const adminNotifications = notifications.filter(notification => 
    notification.type === 'gallery' || notification.type === 'news'
  );

  const isGroup = (conv) => (Array.isArray(conv?.participants) && conv.participants.length > 2) || ((conv?.name || '').toLowerCase().includes('group'));
  const getOtherMember = (conv) => {
    if (!conv || !Array.isArray(conv.participants)) return { id: null, name: '' };
    const other = conv.participants.find(p => Number(p.id) !== Number(currentUserId));
    return other || { id: null, name: conv.name || '' };
  };
  const getConvDisplayName = (conv) => isGroup(conv) ? (conv?.name || 'Group') : (getOtherMember(conv).name || 'Conversation');
  const adminUnreadCount = adminNotifications.filter(notification => !notification.read).length;

  // Deduplicate gallery notifications by imageId (or id) so each request appears only once
  const unreadGalleryNotifications = (() => {
    const seen = new Set();
    const unique = [];
    for (const notif of adminNotifications) {
      if (notif.type !== 'gallery' || notif.read) continue;
      const key = notif.data?.imageId ?? notif.id;
      if (key == null) {
        unique.push(notif);
        continue;
      }
      if (!seen.has(key)) {
        seen.add(key);
        unique.push(notif);
      }
    }
    return unique;
  })();

  const galleryUnreadCount = unreadGalleryNotifications.length;

  // Deduplicate news notifications by newsId (or id) so each request appears only once
  const unreadNewsNotifications = (() => {
    const seen = new Set();
    const unique = [];
    for (const notif of adminNotifications) {
      if (notif.type !== 'news' || notif.read) continue;
      const key = notif.data?.newsId ?? notif.id;
      if (key == null) {
        unique.push(notif);
        continue;
      }
      if (!seen.has(key)) {
        seen.add(key);
        unique.push(notif);
      }
    }
    return unique;
  })();

  const newsUnreadCount = unreadNewsNotifications.length;

  // Global gallery notifications hook
  const { fetchPendingGalleryRequests } = useGlobalGalleryNotifications();

  // Global news notifications hook
  const { fetchPendingNewsRequests } = useGlobalNewsNotifications();

  const handleLogout = () => setLogoutOpen(true);
  const confirmLogout = () => {
    try {
      localStorage.removeItem('currentUser');
      localStorage.removeItem('authToken');
      localStorage.removeItem('userRole');
      localStorage.removeItem('userData');
      sessionStorage.clear();
    } catch (_) {}
    setLogoutOpen(false);
    navigate("/login");
  };

  useEffect(() => {
    let cancelled = false;

    const fetchCounts = async () => {
      if (cancelled) return;
      try {
        const res = await fetch(`https://aaapd.org.et/gallery_backend/api/getAllMembers.php`);
        const data = await res.json();
        const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
        const norm = (s) => (s || '').toString().toLowerCase();
        const approved = list.filter(m => norm(m.aaapd_status) === 'approved');
        const pending = list.filter(m => norm(m.aaapd_status) === 'pending');
        const rejected = list.filter(m => norm(m.aaapd_status) === 'rejected');
        setMembersCount(approved.length);
        setPendingCount(pending.length);
        setRejectedCount(rejected.length);
      } catch (e) {
        if (cancelled) return;
        setMembersCount(0);
        setPendingCount(0);
        setRejectedCount(0);
      }

      try {
        const allUsers = await db.users.toArray();
        if (!cancelled) setUsersCount(allUsers.length);
      } catch (e) {
        if (!cancelled) setUsersCount(0);
      }

      try {
        const res = await fetch('https://aaapd.org.et/gallery_backend/api/get_opds.php');
        const data = await res.json();
        const count = (data && data.status === 'success' && Array.isArray(data.data))
          ? data.data.length
          : (Array.isArray(data) ? data.length : 0);
        if (!cancelled) setOpdsCount(count);
      } catch (e) {
        if (!cancelled) setOpdsCount(0);
      }
    };

    fetchCounts();
    const intervalId = setInterval(fetchCounts, 5000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, []);

  // Fetch pending gallery requests on admin login
  useEffect(() => {
    fetchPendingGalleryRequests();
  }, [fetchPendingGalleryRequests]);

  // Fetch pending news requests on admin login
  useEffect(() => {
    fetchPendingNewsRequests();
  }, [fetchPendingNewsRequests]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      // Close mobile sidebar only if click is outside the sidebar and not the hamburger button
      if (
        isOpen &&
        !event.target.closest('.mobile-menu-button') &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
      // Close language dropdown when clicking outside
      if (languageOpen && languageRef.current && !languageRef.current.contains(event.target)) {
        setLanguageOpen(false);
      }
      // Close notifications dropdown when clicking outside
      if (notificationsOpen && notifRef.current && !notifRef.current.contains(event.target)) {
        setNotificationsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, languageOpen, notificationsOpen]);

  // Poll chats API to compute unread messages count for current admin and cache conversations
  useEffect(() => {
    let timer;
    const fetchUnread = async () => {
      try {
        if (!currentUserId) { setUnreadMessageCount(0); setChatConversations([]); return; }
        const res = await fetch(`https://aaapd.org.et/gallery_backend/api/get_conversations.php?userId=${currentUserId}`);
        const convs = await res.json();
        if (Array.isArray(convs)) {
          const total = convs.reduce((acc, c) => acc + (Number(c.unreadCount) || 0), 0);
          setUnreadMessageCount(total);
          setChatConversations(convs);
        } else {
          setUnreadMessageCount(0);
          setChatConversations([]);
        }
      } catch (_) {
        setUnreadMessageCount(0);
        setChatConversations([]);
      }
    };
    fetchUnread();
    timer = setInterval(fetchUnread, 10000);
    return () => { if (timer) clearInterval(timer); };
  }, [currentUserId]);

  const NavLinkItem = ({ to, icon, label, badge, exact = false }) => (
    <NavLink
      to={to}
      end={exact}
      onClick={() => setIsOpen(false)}
      className={({ isActive }) =>
        `flex items-center justify-between gap-3 px-4 py-2 rounded-lg transition-colors ${
          isActive
            ? "bg-teal-100 text-teal-700 font-semibold"
            : "text-gray-700 hover:bg-gray-100"
        }`
      }
    >
      <div className="flex items-center gap-3">
        {icon}
        <span>{label}</span>
      </div>
      {badge && (
        <span className="text-sm bg-teal-100 text-teal-700 px-2 rounded-full">
          {badge}
        </span>
      )}
    </NavLink>
  );

  const AccordionButton = ({ open, setOpen, icon, label }) => (
    <button
      onClick={() => setOpen(!open)}
      className="w-full flex items-center justify-between gap-3 px-4 py-2 rounded-lg text-gray-700 hover:bg-gray-100 font-medium transition-colors"
    >
      <div className="flex items-center gap-3">
        {icon}
        <span>{label}</span>
      </div>
      <ChevronDown
        className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`}
      />
    </button>
  );

  return (
    <>
      {/* Hamburger Menu for Mobile */}
      <button
        className="md:hidden fixed top-4 left-4 z-[60] text-gray-800 bg-white/50 backdrop-blur-sm p-2 rounded-full shadow-md"
        onClick={() => setIsOpen(true)}
      >
        <Menu className="w-6 h-6" />
      </button>

      {/* Overlay for Mobile */}
      {isOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/40 z-[60]"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        ref={sidebarRef}
        className={`fixed inset-y-0 left-0 z-[70] flex flex-col bg-white shadow-lg transition-transform duration-300 transform ${isOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 md:w-64 w-72`}
      >
        {/* Close button for mobile */}
        <button
          className="md:hidden absolute top-4 right-4 text-gray-600 hover:text-gray-900"
          onClick={() => setIsOpen(false)}
        >
          <X className="w-6 h-6" />
        </button>

        <div className="flex flex-col h-full">
          {/* Logos and Title */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-center gap-4 mb-4">
              <img src="/aaapdmainlogo.jpg" alt="AAAPD-logo" className="h-20 w-auto" />
             
            </div>
            
            
            {/* Online Status Indicator */}
            <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-gray-50">
              <div className="flex items-center gap-2">
                {isOnline ? (
                  <>
                    <div className="relative">
                      <Wifi className="w-4 h-4 text-green-600" />
                      <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    </div>
                    <span className="text-sm font-medium text-green-700">{t('onlineStatus')}</span>
                  </>
                ) : (
                  <>
                    <div className="relative">
                      <WifiOff className="w-4 h-4 text-red-600" />
                      <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                    </div>
                    <span className="text-sm font-medium text-red-700">{t('offlineStatus')}</span>
                  </>
                )}
              </div>

              {/* Bell Notification Icon - shows unread chat messages + new gallery requests */}
              <div className="relative" ref={notifRef}>
                <button
                  onClick={() => setNotificationsOpen(!notificationsOpen)}
                  className="relative p-2 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <Bell className="w-5 h-5 text-gray-600" />
                  {/* Notification Badge (chat + deduplicated gallery + news requests) */}
                  {unreadMessageCount + galleryUnreadCount + newsUnreadCount > 0 && (
                    <div className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-red-500 rounded-full flex items-center justify-center">
                      <span className="text-[10px] leading-none text-white font-bold">{
                        unreadMessageCount + galleryUnreadCount + newsUnreadCount > 99
                          ? '99+'
                          : unreadMessageCount + galleryUnreadCount + newsUnreadCount
                      }</span>
                    </div>
                  )}
                </button>

                {notificationsOpen && (
                  <div className="absolute left-full top-0 ml-2 w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                    <div className="p-4 border-b border-gray-200">
                      <h3 className="font-semibold text-gray-800">{t('notifications')}</h3>
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {unreadGalleryNotifications.length === 0 && unreadNewsNotifications.length === 0 && chatConversations.filter(c => (Number(c.unreadCount) || 0) > 0).length === 0 ? (
                        <div className="p-6 text-center text-gray-500">
                          <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">{t('noNewNotifications')}</p>
                        </div>
                      ) : (
                        <>
                          {/* Gallery request notifications */}
                          {unreadGalleryNotifications.map((notif) => (
                            <div key={notif.id || `${notif.type}-${notif.data?.imageId || Math.random()}`}
                                 className="p-3 hover:bg-gray-50 border-b border-gray-100">
                              <div className="flex items-start gap-3">
                                <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold flex-shrink-0">
                                  <GalleryHorizontal className="w-4 h-4" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between gap-2">
                                    <p className="text-sm font-semibold text-gray-800 truncate">{notif.title || 'Gallery Request'}</p>
                                  </div>
                                  <p className="text-xs text-gray-600 truncate">{notif.message}</p>
                                </div>
                              </div>
                            </div>
                          ))}

                          {/* News request notifications */}
                          {unreadNewsNotifications.map((notif) => (
                            <div key={notif.id || `${notif.type}-${notif.data?.newsId || Math.random()}`}
                                 className="p-3 hover:bg-gray-50 border-b border-gray-100">
                              <div className="flex items-start gap-3">
                                <div className="w-8 h-8 rounded-full bg-yellow-100 text-yellow-700 flex items-center justify-center font-bold flex-shrink-0">
                                  <FileText className="w-4 h-4" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between gap-2">
                                    <p className="text-sm font-semibold text-gray-800 truncate">{notif.title || 'News Request'}</p>
                                  </div>
                                  <p className="text-xs text-gray-600 truncate">{notif.message}</p>
                                </div>
                              </div>
                            </div>
                          ))}

                          {/* Chat message notifications */}
                          {chatConversations
                            .filter(c => (Number(c.unreadCount) || 0) > 0)
                            .sort((a,b) => new Date(b.lastMessageAt) - new Date(a.lastMessageAt))
                            .slice(0, 10)
                            .map(conv => (
                              <div key={conv.id} className="p-3 hover:bg-gray-50 border-b border-gray-100">
                                <div className="flex items-start gap-3">
                                  <div className="w-8 h-8 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center font-bold flex-shrink-0">
                                    {getConvDisplayName(conv).charAt(0).toUpperCase()}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between gap-2">
                                      <p className="text-sm font-semibold text-gray-800 truncate">{getConvDisplayName(conv)}</p>
                                      <span className="ml-2 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{Number(conv.unreadCount) || 0}</span>
                                    </div>
                                    <p className="text-xs text-gray-600 truncate">{conv.lastMessageText || 'New messages'}</p>
                                    <p className="text-[10px] text-gray-400 mt-1">{conv.lastMessageAt ? new Date(conv.lastMessageAt).toLocaleString() : ''}</p>
                                  </div>
                                </div>
                              </div>
                            ))}
                        </>
                      )}
                    </div>
                    <div className="p-3 border-t border-gray-200 text-right">
                      <button onClick={() => { setNotificationsOpen(false); navigate('/adminmessages'); }} className="text-sm text-blue-600 hover:text-blue-800 font-medium">{t('viewAll')}</button>
                    </div>
                  </div>
                )}
              </div>

              <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-green-500' : 'bg-red-500'} animate-pulse`}></div>
            </div>
          </div>

          <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
            {/* Dashboard */}
            <NavLinkItem to="/admindashboard" icon={<PieChart className="w-5 h-5" />} label={t('dashboard')} exact />

            {/* Members Section */}
            <AccordionButton open={membersOpen} setOpen={setMembersOpen} icon={<Users className="w-5 h-5" />} label={t('members')} />
            {membersOpen && (
              <div className="pl-8 flex flex-col gap-1">
                <NavLinkItem to="/adminmembers" label={t('allMembers')} badge={membersCount} exact />
                <NavLinkItem to="/adminmembers/pending" label={t('pendingMembers')} badge={pendingCount} />
                <NavLinkItem to="/adminmembers/rejected" label={t('rejectedMembers')} badge={rejectedCount} />
                <NavLinkItem to="/adminmembers/renewals" label={t('renewals')} />
              </div>
            )}

            {/* OPDs Section */}
            <AccordionButton open={opdsOpen} setOpen={setOpdsOpen} icon={<Building2 className="w-5 h-5" />} label={t('opds')} />
            {opdsOpen && (
              <div className="pl-8 flex flex-col gap-1">
                <NavLinkItem to="/adminopds" label={t('allOpds')} badge={opdsCount} />
              </div>
            )}

            {/* Reports Section */}
            <AccordionButton open={reportsOpen} setOpen={setReportsOpen} icon={<FileText className="w-5 h-5" />} label={t('reports')} />
            {reportsOpen && (
              <div className="pl-8 flex flex-col gap-1">
                <NavLinkItem to="/reports" label={t('viewReports')} />
                <NavLinkItem to="/adminplansreports" label={t('plansAndReports')} />
              </div>
            )}

            {/* Gallery and Blog Management */}
            <AccordionButton open={managementOpen} setOpen={setManagementOpen} icon={<GalleryHorizontal className="w-5 h-5" />} label={t('management')} />
            {managementOpen && (
              <div className="pl-8 flex flex-col gap-1">
                <NavLinkItem to="/galleryadmin" label={t('galleryManagement')} />
                <NavLinkItem to="/adminnews" label={t('blogManagement')} />
                <NavLinkItem to="/adminprograms" label={t('programsManagement')} />
              </div>
            )}

            {/* Messages */}
            <NavLinkItem to="/adminmessages" icon={<MessageSquare className="w-5 h-5" />} label={t('messages')} />

            {/* Support Tracking */}
            <AccordionButton open={supportsOpen} setOpen={setSupportsOpen} icon={<HelpingHand className="w-5 h-5" />} label={t('supportTracking')} />
            {supportsOpen && (
              <div className="pl-8 flex flex-col gap-1">
               
                <NavLinkItem to="/supportperopd" label={t('perOpd')} />
              </div>
            )}

            {/* Settings */}
            <NavLinkItem to="/adminsettings" icon={<Settings className="w-5 h-5" />} label={t('settings')} />
          </nav>

          {/* Footer with Logout */}
          <div className="px-2 py-4 border-t border-gray-200">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2 rounded-lg text-gray-700 hover:bg-gray-100 font-medium"
            >
              <LogOut className="w-5 h-5" />
              <span>{t('logout')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Logout Modal */}
      <Dialog.Root open={logoutOpen} onOpenChange={setLogoutOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/40 z-[1200]" />
          <Dialog.Content className="fixed z-[1201] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg p-6 w-[90vw] max-w-md">
            <Dialog.Title className="text-lg font-bold mb-2">
              {t('logoutConfirmationTitle')}
            </Dialog.Title>
            <Dialog.Description className="mb-4">
              {t('logoutConfirmationMessage')}
            </Dialog.Description>
            <div className="flex justify-end gap-2">
              <Dialog.Close asChild>
                <button className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300">{t('cancel')}</button>
              </Dialog.Close>
              <button
                onClick={confirmLogout}
                className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700"
              >
                {t('yesLogout')}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Floating Language Dropdown - Top Right Corner */}
      <div className="fixed top-4 z-[80]" ref={languageRef} style={{ right: '96px' }}>
        <button
          onClick={() => setLanguageOpen(!languageOpen)}
          className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brown-500 transition duration-300"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
          </svg>
          {language === 'en' ? 'Eng' : 'አማ'}
          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Language Dropdown Menu */}
        <div
          className={`absolute left-0 top-full mt-2 w-24 bg-white border border-gray-200 rounded-md shadow-lg z-50 transition-all duration-300 transform -translate-x-1/2 ${
            languageOpen ? "opacity-100 visible" : "opacity-0 invisible"
          }`}
        >
          <div className="py-1">
            <button
              onClick={() => {
                changeLanguage('en');
                setLanguageOpen(false);
              }}
              className={`w-full text-left px-4 py-2 text-sm transition duration-300 ${
                language === 'en'
                  ? 'bg-brown-50 text-brown-600 font-medium'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              Eng
            </button>
            <button
              onClick={() => {
                changeLanguage('am');
                setLanguageOpen(false);
              }}
              className={`w-full text-left px-4 py-2 text-sm transition duration-300 ${
                language === 'am'
                  ? 'bg-brown-50 text-brown-600 font-medium'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              አማ
            </button>
          </div>
        </div>
      </div>

      {/* Accessibility Controls - Top Right Corner */}
      <FloatingAccessibilityControls position="top-right" />
    </>
  );
}
